import { Button, Card } from '@/components/ui';
import { EntertainmentPlayerName } from '@/components/entertainment/EntertainmentPlayerName';
import type { SharedCostDraft } from '@/lib/entertainment/ledger-types';
import type { User } from '@/lib/types';
import { formatCurrency } from '@/lib/utils';

type Props = {
  draft: SharedCostDraft;
  users: User[];
  categoryName: string;
  onBack: () => void;
  onConfirm: () => void;
};

function sharesForDraft(draft: SharedCostDraft) {
  const positiveWeights = Object.entries(draft.weights).filter(([, weight]) => weight > 0);
  const totalWeight = positiveWeights.reduce((sum, [, weight]) => sum + weight, 0);
  let allocated = 0;
  const shares = Object.fromEntries(
    Object.entries(draft.weights).map(([userId, weight]) => {
      const share = weight > 0 && totalWeight > 0
        ? Math.floor((draft.amount_cents * weight) / totalWeight)
        : 0;
      allocated += share;
      return [userId, share];
    }),
  );
  let remainder = draft.amount_cents - allocated;
  for (const [userId] of positiveWeights) {
    if (remainder <= 0) break;
    shares[userId] += 1;
    remainder -= 1;
  }
  return shares;
}

export function SharedCostReview({ draft, users, categoryName, onBack, onConfirm }: Props) {
  const shares = sharesForDraft(draft);
  const totalWeight = Object.values(draft.weights).reduce((sum, weight) => sum + weight, 0);
  const payer = users.find((user) => user.id === draft.payer_id);

  return (
    <div data-testid="shared-cost-review">
      <Card className="p-4">
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-sm font-bold text-aura-primary mb-3 uppercase tracking-widest">Shared Cost Review</h2>
          <div className="mt-1 text-lg font-extrabold text-text-main">{draft.description}</div>
        </div>

        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-bold uppercase tracking-widest text-aura-primary/70">Total Cost</dt>
            <dd className="text-sm font-bold text-text-main">{formatCurrency(draft.amount_cents / 100)}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-widest text-aura-primary/70">Payer Credit</dt>
            <dd className="text-sm font-bold text-status-success">{formatCurrency(draft.amount_cents / 100)}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-widest text-aura-primary/70">Payer</dt>
            <dd className="mt-1">
              <EntertainmentPlayerName user={payer} fallbackId={draft.payer_id} />
            </dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-widest text-aura-primary/70">Category</dt>
            <dd className="text-sm font-bold text-text-main">{categoryName}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-widest text-aura-primary/70">Total Weight</dt>
            <dd className="text-sm font-bold text-text-main">{totalWeight.toFixed(2)}</dd>
          </div>
        </dl>

        <div className="flex flex-col gap-2">
          {Object.entries(shares).filter(([, share]) => share > 0).map(([userId, share]) => {
            const user = users.find((item) => item.id === userId);
            return (
              <div key={userId} className="flex items-center justify-between rounded-md border border-border-subtle p-2">
                <EntertainmentPlayerName user={user} fallbackId={userId} />
                <span className="text-sm font-bold text-status-error">{formatCurrency(share / 100)}</span>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>Back</Button>
          <Button type="button" size="sm" className="text-white" onClick={onConfirm}>Confirm Shared Cost</Button>
        </div>
      </div>
      </Card>
    </div>
  );
}
